/**
 * Builds an off-screen HTML node that reproduces the "Attrition Insights Report"
 * design. The node is meant to be captured with html2canvas and placed into a
 * jsPDF document. All styling is inline so capture is independent of app CSS.
 */

// Brand palette used across cards, charts and distribution bars.
const PALETTE = [
  "#5b5bd6", // indigo
  "#17b0a0", // teal
  "#f5a623", // amber
  "#f5731f", // orange
  "#9b8cff", // light purple
  "#f0506e", // red/pink
  "#36a2eb",
  "#4bc0c0",
  "#9966ff",
  "#2ecc71",
];

const REPORT_WIDTH = 920; // px, roughly A4 proportion at the chosen scale

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const pct = (value, total) =>
  total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";

/**
 * Turn grouped_data into a sorted list with a rolled-up "Others" bucket so the
 * tables and charts stay readable, matching the sample report (top 5 + Others).
 */
const buildRows = (groupedData, { maxRows = 5 } = {}) => {
  const entries = Object.entries(groupedData || {}).map(([name, value]) => ({
    name,
    value: Number(value) || 0,
  }));
  entries.sort((a, b) => b.value - a.value);

  if (entries.length <= maxRows + 1) return entries;

  const head = entries.slice(0, maxRows);
  const rest = entries.slice(maxRows);
  const othersValue = rest.reduce((sum, r) => sum + r.value, 0);
  head.push({ name: "Others", value: othersValue });
  return head;
};

/** A category section can also be rendered with a fixed order (e.g. tenure bands). */
const buildOrderedRows = (groupedData, order) =>
  order.map((name) => ({ name, value: Number(groupedData?.[name]) || 0 }));

const CATEGORY_META = {
  company_insights: {
    title: "Company Insights",
    subtitle: "Companies recorded in the period",
    column: "Company",
    chart: "donut",
  },
  location_insights: {
    title: "Location Insights",
    subtitle: "Locations recorded in the period",
    column: "Location",
    chart: "bar",
  },
  designation_insights: {
    title: "Designation Insights",
    subtitle: "Job titles recorded in the period",
    column: "Designation",
    chart: "bar",
  },
  tenure_insights: {
    title: "Tenure Insights",
    subtitle: "Length of service at exit",
    column: "Tenure band",
    chart: "bar",
    order: ["0-1 years", "1-3 years", "3-5 years", "5+ years"],
  },
};

/* ----------------------------- SVG charts ----------------------------- */

const donutSVG = (rows, total) => {
  const size = 220;
  const stroke = 42;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  const segments = rows
    .map((row, i) => {
      const fraction = total > 0 ? row.value / total : 0;
      const dash = fraction * circumference;
      const gap = circumference - dash;
      const el = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none"
        stroke="${PALETTE[i % PALETTE.length]}" stroke-width="${stroke}"
        stroke-dasharray="${dash} ${gap}"
        stroke-dashoffset="${-offset}" transform="rotate(-90 ${cx} ${cy})" />`;
      offset += dash;
      return el;
    })
    .join("");

  return `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      ${segments}
      <text x="${cx}" y="${cy - 4}" text-anchor="middle"
        font-size="34" font-weight="700" fill="#1f2a44">${total}</text>
      <text x="${cx}" y="${cy + 20}" text-anchor="middle"
        font-size="13" fill="#8a93a6">records</text>
    </svg>`;
};

const barSVG = (rows) => {
  const width = 300;
  const height = 220;
  const padBottom = 34;
  const padTop = 20;
  const maxVal = Math.max(1, ...rows.map((r) => r.value));
  const slot = width / rows.length;
  const barW = Math.min(44, slot * 0.55);

  const bars = rows
    .map((row, i) => {
      const h = ((row.value / maxVal) * (height - padBottom - padTop)) | 0;
      const x = i * slot + (slot - barW) / 2;
      const y = height - padBottom - h;
      const label = esc(row.name).slice(0, 12);
      return `
        <rect x="${x}" y="${y}" width="${barW}" height="${Math.max(h, 2)}"
          rx="6" fill="${PALETTE[i % PALETTE.length]}" />
        <text x="${x + barW / 2}" y="${y - 6}" text-anchor="middle"
          font-size="13" font-weight="700" fill="#1f2a44">${row.value}</text>
        <text x="${x + barW / 2}" y="${height - 14}" text-anchor="middle"
          font-size="10" fill="#8a93a6">${label}</text>`;
    })
    .join("");

  return `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${bars}</svg>`;
};

/* ----------------------------- Table ----------------------------- */

const tableHTML = (rows, total, columnLabel) => {
  const body = rows
    .map((row, i) => {
      const color = PALETTE[i % PALETTE.length];
      const share = pct(row.value, total);
      return `
      <tr>
        <td style="padding:10px 8px;">
          <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};"></span>
        </td>
        <td style="padding:10px 8px;color:#1f2a44;font-weight:500;">${esc(row.name)}</td>
        <td style="padding:10px 8px;text-align:right;color:#1f2a44;font-weight:700;">${row.value}</td>
        <td style="padding:10px 8px;text-align:right;color:#8a93a6;">${share}%</td>
        <td style="padding:10px 8px;width:180px;">
          <div style="background:#eef0f5;border-radius:6px;height:10px;width:100%;">
            <div style="background:${color};height:10px;border-radius:6px;width:${share}%;"></div>
          </div>
        </td>
      </tr>`;
    })
    .join("");

  return `
    <table style="width:100%;border-collapse:collapse;font-size:13px;">
      <thead>
        <tr style="background:#f6f7fb;color:#8a93a6;text-align:left;">
          <th style="padding:10px 8px;width:24px;">#</th>
          <th style="padding:10px 8px;">${esc(columnLabel)}</th>
          <th style="padding:10px 8px;text-align:right;">Count</th>
          <th style="padding:10px 8px;text-align:right;">Share</th>
          <th style="padding:10px 8px;">Distribution</th>
        </tr>
      </thead>
      <tbody>${body}</tbody>
      <tfoot>
        <tr style="border-top:2px solid #e6e8f0;">
          <td></td>
          <td style="padding:10px 8px;color:#1f2a44;font-weight:700;">Total</td>
          <td style="padding:10px 8px;text-align:right;color:#1f2a44;font-weight:700;">${total}</td>
          <td style="padding:10px 8px;text-align:right;color:#1f2a44;font-weight:700;">100.0%</td>
          <td></td>
        </tr>
      </tfoot>
    </table>`;
};

/* ----------------------------- Section + cards ----------------------------- */

const sectionHTML = (meta, rows, total) => {
  const chart =
    meta.chart === "donut" ? donutSVG(rows, total) : barSVG(rows);

  return `
    <div data-block="section" style="background:#fff;border:1px solid #edeff4;border-radius:14px;
      padding:22px;margin:0 22px 18px;box-shadow:0 1px 2px rgba(16,24,40,.04);">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;">
        <div>
          <div style="font-size:19px;font-weight:700;color:#1f2a44;">${esc(meta.title)}</div>
          <div style="font-size:12px;color:#8a93a6;margin-top:2px;">${esc(meta.subtitle)}</div>
        </div>
        <div style="background:#eef0f5;border-radius:999px;padding:5px 12px;
          font-size:12px;font-weight:700;color:#1f2a44;">Total: ${total}</div>
      </div>
      <div style="display:flex;gap:24px;align-items:center;margin-top:16px;">
        <div style="flex:0 0 auto;display:flex;justify-content:center;align-items:center;min-width:240px;">
          ${chart}
        </div>
        <div style="flex:1 1 auto;">${tableHTML(rows, total, meta.column)}</div>
      </div>
    </div>`;
};

const statCard = ({ value, label, caption, accent }) => `
  <div style="flex:1;background:#fff;border:1px solid #edeff4;border-top:3px solid ${accent};
    border-radius:12px;padding:16px 18px;box-shadow:0 1px 2px rgba(16,24,40,.04);">
    <div style="font-size:30px;font-weight:800;color:#1f2a44;line-height:1;">${esc(value)}</div>
    <div style="font-size:13px;font-weight:700;color:#1f2a44;margin-top:8px;">${esc(label)}</div>
    <div style="font-size:11px;color:#8a93a6;margin-top:3px;">${esc(caption)}</div>
  </div>`;

/** Top label like "Tata Coffee / Tata Elxsi (3 each)". */
const topCaption = (rows, { suffix = "" } = {}) => {
  if (!rows.length) return "No records";
  const topValue = rows[0].value;
  const leaders = rows.filter((r) => r.value === topValue && r.name !== "Others");
  if (!leaders.length) return "No records";
  const names = leaders.map((r) => r.name).slice(0, 2).join(" / ");
  if (leaders.length > 1) return `Top: ${names} (${topValue} each)`;
  const share = rows.__total ? ` (${topValue}, ${pct(topValue, rows.__total)}%)` : "";
  return `Top: ${names}${share}${suffix}`;
};

/* ----------------------------- Public builder ----------------------------- */

/**
 * @param {object} args
 * @param {object} args.chartData   API response (grouped_data per category)
 * @param {string} args.companyName label shown in the header ("Issuer")
 * @param {string} args.period      e.g. "Last 1 Year"
 * @returns {HTMLElement} detached node ready for html2canvas
 */
export const buildInsightsReportNode = ({ chartData, companyName, period }) => {
  const generated = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  // Prepare per-category rows once so cards and sections stay consistent.
  const categories = Object.keys(CATEGORY_META)
    .map((key) => {
      const data = chartData?.[key];
      const grouped = data?.grouped_data;
      if (!grouped || Object.keys(grouped).length === 0) return null;
      const total = Number(data.total_count) || 0;
      const meta = CATEGORY_META[key];
      const rows = meta.order
        ? buildOrderedRows(grouped, meta.order)
        : buildRows(grouped);
      rows.__total = total;
      return { key, meta, rows, total };
    })
    .filter(Boolean);

  const find = (key) => categories.find((c) => c.key === key);
  const company = find("company_insights");
  const designation = find("designation_insights");
  const tenure = find("tenure_insights");

  const cards = [];
  if (company)
    cards.push(
      statCard({
        value: company.total,
        label: "Company records",
        caption: topCaption(company.rows),
        accent: PALETTE[0],
      })
    );
  if (designation)
    cards.push(
      statCard({
        value: designation.total,
        label: "Designation records",
        caption: topCaption(designation.rows),
        accent: PALETTE[1],
      })
    );
  if (tenure)
    cards.push(
      statCard({
        value: tenure.total,
        label: "Tenure records",
        caption: topCaption(tenure.rows),
        accent: PALETTE[2],
      })
    );
  if (tenure) {
    const under1 = Number(tenure.rows.find((r) => r.name === "0-1 years")?.value) || 0;
    cards.push(
      statCard({
        value: under1,
        label: "Exits under 1 year",
        caption: `0-1 years bucket (${pct(under1, tenure.total)}%)`,
        accent: PALETTE[3],
      })
    );
  }

  const sections = categories
    .map(({ meta, rows, total }) => sectionHTML(meta, rows, total))
    .join("");

  const footerNote = (() => {
    const counts = categories.map((c) => c.total).join(" / ");
    const names = categories.map((c) => c.meta.column.toLowerCase()).join(" / ");
    if (!counts) return "No records available for the selected period.";
    return `Record counts differ per category (${counts}): each category is counted on its own field (${names}).`;
  })();

  const wrapper = document.createElement("div");
  wrapper.style.position = "fixed";
  wrapper.style.left = "-10000px";
  wrapper.style.top = "0";
  wrapper.style.width = `${REPORT_WIDTH}px`;
  wrapper.style.background = "#f3f4f8";
  wrapper.style.fontFamily =
    "Inter, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

  wrapper.innerHTML = `
    <div style="background:#f3f4f8;padding-bottom:22px;">
      <div data-block="header" style="background:#1c2434;color:#fff;padding:26px 28px;
        display:flex;justify-content:space-between;align-items:flex-start;">
        <div>
          <div style="font-size:26px;font-weight:800;">Attrition Insights Report</div>
          <div style="font-size:12px;color:#aeb6c6;margin-top:8px;">
            Company: ${esc(companyName)} &nbsp;|&nbsp; Period: ${esc(period)} &nbsp;|&nbsp; Generated: ${esc(generated)}
          </div>
        </div>
        <div style="font-size:18px;font-weight:800;letter-spacing:.3px;">AuthNumber</div>
      </div>

      <div data-block="cards" style="display:flex;gap:14px;padding:20px 22px;">${cards.join("")}</div>

      ${sections}

      <div data-block="footer" style="display:flex;justify-content:space-between;
        padding:4px 24px 0;font-size:11px;color:#8a93a6;">
        <span>${esc(footerNote)}</span>
        <span>Attrition Insights | ${esc(companyName)} | ${esc(period)}</span>
      </div>
    </div>`;

  return wrapper;
};
